import { getCookie } from "@insite/client-framework/Common/Cookies";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFlyoutDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetFlyoutDrawerIsOpen";
import setInitialValues from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetInitialValues";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import setSignInOverlayIsOpen from "@insite/client-framework/Store/Components/SignInOverlay/Handlers/SetSignInOverlayIsOpen";
import {
    getCurrencies,
    getFulfillmentLabel,
    getIsPunchOutSession,
    getLanguages,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import setCurrency from "@insite/client-framework/Store/Context/Handlers/SetCurrency";
import setLanguage from "@insite/client-framework/Store/Context/Handlers/SetLanguage";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getHeader, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import { getPageLinkByPageType, LinkModel, mapLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import ShowInventoryToggle, { ShowInventoryToggleStyles } from "@insite/content-library/Components/ShowInventoryToggle";
import ShowPricingToggle, { ShowPricingToggleStyles } from "@insite/content-library/Components/ShowPricingToggle";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconProps } from "@insite/mobius/Icon";
import Globe from "@insite/mobius/Icons/Globe";
import MapPin from "@insite/mobius/Icons/MapPin";
import Menu from "@insite/mobius/Icons/Menu";
import LazyImage from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import PanelRow, { PanelRowPresentationProps } from "@insite/mobius/PanelMenu/PanelRow";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import omitSingle from "@insite/mobius/utilities/omitSingle";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { MappedLink as MenuMappedLink } from "@tKgpco/Components/Menu";
import PanelMenu, { PanelMenuPresentationProps } from "@tKgpco/MobiusOverrides/PanelMenu";
import { MappedLink } from "@tKgpco/Overrides/Widgets/Header/MainNavigation";
import { COLORS } from "@tKgpco/theme/design";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css, ThemeProps, withTheme } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const linkListStateLinks = getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Header/HeaderLinkList";
    })?.fields.links;
    const displayPricingAndInventory = state.context.session?.displayPricingAndInventory || false;
    const currentMode = getCookie(NavigationModeCookieName) || "Storefront";

    return {
        currencies: getCurrencies(state),
        currentCurrencyId: state.context.session.currency?.id,
        currentCurrencySymbol: state.context.session.currency?.currencySymbol,
        languages: getLanguages(state),
        currentLanguage: state.context.session.language,
        currentLocation: getLocation(state),
        userName: state.context.session?.userName,
        isGuest: state.context.session?.isGuest,
        myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
        signInUrl: getPageLinkByPageType(state, "SignInPage"),
        headerLinkListLinks: mapLinks<LinkModel, { openInNewWindow: boolean }>(
            state,
            linkListStateLinks,
            widgetLink => ({
                openInNewWindow: widgetLink.fields.openInNewWindow,
            }),
        ),
        showCustomerMenuItem: getSettingsCollection(state).accountSettings.enableWarehousePickup,
        fulfillmentLabel: getFulfillmentLabel(state),
        navDrawerIsOpen: state.components.addressDrawer.navDrawerIsOpen,
        flyoutDrawerIsOpen: state.components.addressDrawer.flyoutDrawerIsOpen,
        isPunchOutSession: getIsPunchOutSession(state),
        visibilityState: findVisibilityState(state.data.pages.widgetsById),
        displayPricingAndInventoryToggles: displayPricingAndInventory && currentMode === "Storefront",
        fieldstoHide: state.context.settings.properties.fieldsToHide || "",
        canshowPrice: state.context?.session?.properties?.canShowPrice?.toLowerCase() === "true",
    };
};

const mapDispatchToProps = {
    setCurrency,
    setLanguage,
    signOut,
    cancelPunchOut,
    setInitialValues,
    setNavDrawerIsOpen,
    setFlyoutDrawerIsOpen,
    setSignInOverlayIsOpen,
};

interface WebsiteDetailsModel {
    webSiteFlag: string;
    webSiteLink: string;
    webSiteTitle: string;
}

interface OwnProps {
    links: MappedLink[];
    showQuickOrder: boolean;
    quickOrderLink: PageLinkModel | undefined;
    websiteDetails: WebsiteDetailsModel;
    displayVmiNavigation?: boolean;
    vmiPageLinks?: (PageLinkModel | undefined)[];
    displayModeSwitch?: boolean;
    currentMode?: string;
    isCompactHeaderFlyout?: boolean;
    showFlyoutPricingToggle?: boolean;
    showFlyoutInventoryToggle?: boolean;
    rootElementSelector?: string;
    allowOpenParentLinks?: boolean;
}

interface NavigationDrawerState extends Pick<NavigationDrawerProps, "currentLocation"> {
    open?: boolean;
    urlLangCodeState: any | undefined;
}

type NavigationDrawerProps = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    ThemeProps<BaseTheme>;

export interface NavigationDrawerStyles {
    menuTriggerButton?: ButtonPresentationProps;
    drawer?: DrawerPresentationProps;
    drawerSectionWrapper?: InjectableCss;
    currencySelectorWrapper?: InjectableCss;
    languageSelectorWrapper?: InjectableCss;
    panelMenu?: PanelMenuPresentationProps;
    panelSectionWrapper?: InjectableCss;
    menuRowIcon?: IconProps;
    userRowTypography?: TypographyPresentationProps;
    punchOutUserRowText?: TypographyPresentationProps;
    mainNavigationRow?: PanelRowPresentationProps;
    mainNavigationRowIcon?: IconProps;
    punchOutNavigationRowIcon?: IconProps;
    mainNavigationRowTypography?: TypographyPresentationProps;
    logoLinks?: PanelRowPresentationProps & { typographyProps: TypographyPresentationProps };
    currencySymbol?: TypographyPresentationProps;
    changeCustomerRow?: PanelRowPresentationProps;
    changeCustomerRowContainer?: GridContainerProps;
    fulfillmentMethodGridItem?: GridItemProps;
    addressesGridItem?: GridItemProps;
    pickUpAddressGridItem?: GridItemProps;
    applyButtonGridItem?: GridItemProps;
    showInventoryToggle?: ShowInventoryToggleStyles;
    showPricingToggle?: ShowPricingToggleStyles;
    signOutRow?: PanelRowPresentationProps;
    signOutRowText?: TypographyPresentationProps;
    hamburgerIcon?: IconProps;
    websiteWrapper?: InjectableCss;
    quickOrder?: InjectableCss;
}

const StyledSection = getStyledWrapper("section");
const StyledSpan = getStyledWrapper("span");

export const navigationDrawerStyles: NavigationDrawerStyles = {
    menuTriggerButton: {
        shape: "pill",
        color: "secondary",
        size: 50,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            background: none;
                            border: none;
                            padding: 0;
                        `,
                    ],
                    "max",
                )}

            &:focus {
                outline: none;
            }
        `,
    },
    drawer: {
        size: 300,
        cssOverrides: {
            drawerContent: css`
                margin-top: -2px;
                overflow-x: hidden;
            `,
        },
    },
    drawerSectionWrapper: {
        css: css`
            &:first-child {
                margin-top: 0;
                padding-bottom: 10px;
                background: ${getColor("common.backgroundContrast")};
            }
            &:last-child {
                margin-bottom: 5px;
            }
            margin-top: 20px;
        `,
    },
    panelMenu: {
        cssOverrides: {
            wrapper: css`
                text-align: left;
                > button {
                    width: calc(100% - 2px);
                }
            `,
            menu: css`
                button {
                    background: #f6f6f6;
                    color: #000;
                    width: 100%;
                }
            `,
            children: css`
                /* { */
                & > :first-child {
                    button:first-child {
                        padding: 10px 12px;
                        justify-content: flex-start;
                        background: none;
                        width: auto;
                        border: 0;
                        background: transparent;
                        span {
                            color: ${COLORS.white};
                        }
                        & :focus {
                            outline-width: 0px;
                        }
                    }
                    button:last-child {
                        border: 1px solid red;
                        padding: 5px 12px;
                        justify-content: flex-end;
                        background: none;
                        width: auto;
                        border: 0;
                        background: transparent;
                        span {
                            color: ${COLORS.white};
                        }
                        & :focus {
                            outline-width: 0px;
                        }
                    }
                    /* } */
                }
                [class^="PanelMenuStyle"] {
                    background: ${COLORS.white};
                    > a {
                        background: ${COLORS.white};
                        span {
                            color: ${COLORS.black};
                        }
                    }
                }
            `,
        },
    },
    panelSectionWrapper: {
        css: css`
            height: 22px;
            display: flex;
            align-items: center;
        `,
    },
    menuRowIcon: {
        size: 22,
        css: css`
            margin-top: -2px;
            margin-right: 10px;
            width: 24px;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
        `,
    },
    userRowTypography: {
        transform: "uppercase",
        color: "common.background",
        css: css`
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        `,
    },
    punchOutUserRowText: {
        transform: "uppercase",
        color: "common.background",
    },
    mainNavigationRow: {
        color: "common.backgroundContrast",
        css: css`
            margin: 2px;
        `,
    },
    mainNavigationRowIcon: {
        src: "User",
        color: "common.background",
        size: 22,
        css: css`
            margin-top: -2px;
            margin-right: 10px;
            width: 24px;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
        `,
    },
    punchOutNavigationRowIcon: {
        src: "User",
        color: "common.background",
        size: 22,
        css: css`
            margin-top: -2px;
            margin-right: 10px;
            width: 24px;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
        `,
    },
    mainNavigationRowTypography: {
        transform: "uppercase",
        color: "common.background",
        weight: "bold",
    },
    logoLinks: {
        color: "common.accent",
        typographyProps: {
            ellipsis: true,
            transform: "uppercase",
        },
        css: css`
            margin: 2px;
        `,
    },
    currencySymbol: {
        size: 22,
        css: css`
            margin-top: -4px;
            margin-right: 10px;
            width: 24px;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
        `,
    },
    changeCustomerRow: {
        css: css`
            &:focus {
                outline: none;
            }
        `,
    },
    changeCustomerRowContainer: { gap: 15 },
    fulfillmentMethodGridItem: { width: 12 },
    addressesGridItem: { width: 12 },
    pickUpAddressGridItem: { width: 12 },
    applyButtonGridItem: { width: 12 },
    showInventoryToggle: {
        toggleClickable: {
            css: css`
                text-transform: uppercase;
                padding: 10px 10px 10px 20px;
            `,
        },
    },
    showPricingToggle: {
        toggleClickable: {
            css: css`
                text-transform: uppercase;
                padding: 10px 10px 10px 20px;
            `,
        },
    },
    signOutRowText: {
        transform: "uppercase",
    },
    quickOrder: {
        css: css`
            padding: 10px 10px 10px 20px;
            span {
                text-transform: uppercase;
                color: #000000 !important;
            }
        `,
    },
    hamburgerIcon: {
        css: css`
            color: #000000 !important;
        `,
    },
    websiteWrapper: {
        css: css`
            padding: 10px 10px 10px 20px;
            img {
                margin-right: 10px;
                margin-left: 5px;
                height: 18px;
                width: 18px;
                z-index: -1;
            }
        `,
    },
};

const styles = navigationDrawerStyles;

class NavigationDrawer extends React.Component<NavigationDrawerProps, NavigationDrawerState> {
    constructor(props: NavigationDrawerProps) {
        super(props);
        this.state = {
            currentLocation: props.currentLocation,
            urlLangCodeState: "",
        };
    }

    componentDidMount() {
        const localStoargeValue = window.localStorage.getItem("ptLanguage");
        const path = window.location.search;
        const search = new URLSearchParams(path);
        const langValue = search.get("__ptLanguage");
        const data = this.props?.languages?.filter(v => v.languageCode === langValue);
        const urlData = this.props?.languages?.filter(v => v.languageCode === localStoargeValue);
        const urlLanguageId = urlData && urlData[0]?.languageCode ? urlData[0]?.languageCode : undefined;
        const dataLanguageId = data && data[0]?.languageCode ? data[0]?.languageCode : localStoargeValue;

        if (path) {
            if (getCookie("SetContextLanguageCode") !== localStoargeValue) {
                this.setState({
                    urlLangCodeState: urlLanguageId,
                });
            }
            if (langValue !== localStoargeValue || getCookie("SetContextLanguageCode") === localStoargeValue) {
                this.setState({
                    urlLangCodeState: dataLanguageId,
                });
            }
        }
        if (!path) {
            if (getCookie("SetContextLanguageCode") !== localStoargeValue) {
                this.setState({
                    urlLangCodeState:
                        urlLanguageId && this.props.currentLanguage?.languageCode
                            ? urlLanguageId
                            : this.props.currentLanguage?.languageCode,
                });
            } else {
                this.setState({
                    urlLangCodeState: dataLanguageId,
                });
            }
        }
    }

    openDrawer = () => {
        // this.props.setNavDrawerIsOpen({ navDrawerIsOpen: true });
        this.props.isCompactHeaderFlyout
            ? this.props.setFlyoutDrawerIsOpen({ isOpen: true })
            : this.props.setNavDrawerIsOpen({ navDrawerIsOpen: true });
        this.props.setInitialValues({});
    };

    closeDrawer = () => {
        // this.props.setNavDrawerIsOpen({ navDrawerIsOpen: false });
        // setTimeout(() => this.props.setNavDrawerIsOpen({ navDrawerIsOpen: undefined }), 300);

        if (this.props.isCompactHeaderFlyout) {
            this.props.setFlyoutDrawerIsOpen({ isOpen: false });
            setTimeout(() => this.props.setFlyoutDrawerIsOpen({ isOpen: undefined }), 300);
            return;
        }

        this.props.setNavDrawerIsOpen({ navDrawerIsOpen: false });
        setTimeout(() => this.props.setNavDrawerIsOpen({ navDrawerIsOpen: undefined }), 300);
    };

    onSignOutHandler = () => {
        this.props.setNavDrawerIsOpen({ navDrawerIsOpen: false });
        this.props.isPunchOutSession ? this.props.cancelPunchOut() : this.props.signOut();
    };

    loadSubLinkIntoCategories = (mappedLink: MappedLink) => {
        mappedLink.children = [];
        const subLinkArray = mappedLink.subLinks && mappedLink.subLinks.split(",");
        if (subLinkArray !== undefined) {
            for (let i = 0; i < subLinkArray?.length; i++) {
                if (subLinkArray[i] !== "") {
                    const id = subLinkArray[i]?.replace(/(\r\n|\n|\r| )/g, "");
                    const linkurl = `${mappedLink?.url}?id=${id}`;
                    const childLink: MappedLink = {
                        url: linkurl,
                        title: subLinkArray[i]?.replace(/(\r\n|\n|\r)/g, ""),
                        children: [],
                    };
                    mappedLink.children.push(childLink);
                }
            }
        }
    };

    signInPanelClickHandler = (event: React.MouseEvent) => {
        if (this.props.theme.signIn?.useOverlay) {
            event.preventDefault();
            setSignInOverlayIsOpen({ isOpen: true });
        }

        this.closeDrawer();
    };

    signInPanelKeyPressHandler = (event: React.KeyboardEvent) => {
        if (event.key === " " && this.props.history && this.props.signInUrl?.url) {
            event.preventDefault();

            if (this.props.theme.signIn?.useOverlay) {
                setSignInOverlayIsOpen({ isOpen: true });
            } else {
                this.props.history.push(this.props.signInUrl.url);
            }

            this.closeDrawer();
        }
    };

    render() {
        const {
            quickOrderLink,
            links,
            showQuickOrder,
            currencies,
            currentCurrencyId,
            currentCurrencySymbol,
            languages,
            currentLanguage,
            setLanguage,
            setCurrency,
            userName,
            isGuest,
            myAccountPageLink,
            signInUrl,
            currentLocation,
            showCustomerMenuItem,
            fulfillmentLabel,
            isPunchOutSession,
            websiteDetails,
            history,
            theme,
            displayPricingAndInventoryToggles,
            displayVmiNavigation,
            vmiPageLinks,
            isCompactHeaderFlyout,
            showFlyoutPricingToggle,
            showFlyoutInventoryToggle,
            rootElementSelector,
            flyoutDrawerIsOpen,
            navDrawerIsOpen,
            setSignInOverlayIsOpen,
            visibilityState,
            allowOpenParentLinks,
        } = this.props;
        const showIcon = visibilityState === "both" || visibilityState === "icon";
        const showLabel = visibilityState === "both" || visibilityState === "label";

        const currentPageUrl = currentLocation.pathname;
        const isOpen = isCompactHeaderFlyout ? flyoutDrawerIsOpen : navDrawerIsOpen;

        return (
            <>
                <Button
                    className="menu-trigger-button"
                    onClick={this.openDrawer}
                    {...styles.menuTriggerButton}
                    data-test-selector="expandMobileMenu"
                >
                    <ButtonIcon className="menu-trigger-button-icon" src={Menu} {...styles.hamburgerIcon} />
                    <VisuallyHidden>{translate("menu")}</VisuallyHidden>
                </Button>
                <Drawer
                    draggable
                    position="left"
                    {...styles.drawer}
                    isOpen={isOpen}
                    handleClose={this.closeDrawer}
                    contentLabel="menu drawer"
                    data-test-selector="drawerSection"
                    rootElementSelector={rootElementSelector}
                >
                    <StyledSection className="drawer-section-wrapper" {...styles.drawerSectionWrapper}>
                        {userName && !isGuest && !isPunchOutSession ? (
                            <PanelMenu
                                className="panel-menu"
                                currentUrl={currentPageUrl}
                                panelTrigger={
                                    <PanelRow className="main-navigation-row" hasChildren {...styles.mainNavigationRow}>
                                        <StyledSpan className="panel-section-wrapper" {...styles.panelSectionWrapper}>
                                            <Icon
                                                className="main-navigation-row-icon"
                                                {...styles.mainNavigationRowIcon}
                                            />
                                            <Typography className="user-row-text" {...styles.userRowTypography}>
                                                {userName}
                                            </Typography>
                                        </StyledSpan>
                                    </PanelRow>
                                }
                                menuItems={
                                    myAccountPageLink?.children
                                        ? myAccountPageLink?.children?.filter(child => {
                                              const title = child?.title?.trim();

                                              // Safely split and normalize excluded titles
                                              const excludedTitles =
                                                  !this.props.canshowPrice && this.props.fieldstoHide
                                                      ? this.props.fieldstoHide
                                                            .split(",")
                                                            .map(t => t.trim().toLowerCase())
                                                      : [];

                                              if (!title) {
                                                  return true;
                                              }

                                              return !excludedTitles.includes(title.toLowerCase());
                                          })
                                        : []
                                }
                                maxDepth={3}
                                closeOverlay={this.closeDrawer}
                                layer={0}
                                {...styles.panelMenu}
                            />
                        ) : isPunchOutSession ? (
                            <PanelRow className="main-navigation-row" {...styles.mainNavigationRow}>
                                <StyledSpan className="panel-section-wrapper" {...styles.panelSectionWrapper}>
                                    <Icon
                                        className="punch-out-navigation-row-icon"
                                        {...styles.punchOutNavigationRowIcon}
                                    />
                                    <Typography className="punch-out-user-row-text" {...styles.punchOutUserRowText}>
                                        {userName}
                                    </Typography>
                                </StyledSpan>
                            </PanelRow>
                        ) : (
                            <PanelRow
                                className="main-navigation-row"
                                {...styles.mainNavigationRow}
                                isCurrent={currentPageUrl === signInUrl?.url}
                                onClick={this.signInPanelClickHandler}
                                onKeyPress={this.signInPanelKeyPressHandler}
                                href={signInUrl?.url}
                            >
                                <StyledSpan className="panel-section-wrapper" {...styles.panelSectionWrapper}>
                                    {showIcon && (
                                        <Icon className="main-navigation-row-icon" {...styles.mainNavigationRowIcon} />
                                    )}
                                    {showLabel && (
                                        <Typography className="user-row-text" {...styles.userRowTypography}>
                                            {translate("Sign In")}
                                        </Typography>
                                    )}
                                </StyledSpan>
                            </PanelRow>
                        )}
                        {/* covers MainNavigation functionality */}
                        {!displayVmiNavigation &&
                            links.map((link, index) => {
                                if (link.subLinks) {
                                    const customMenu = this.props.links.filter(
                                        link => link.childrenType === "CustomMenu",
                                    );
                                    for (let i = 0; i < customMenu.length; i++) {
                                        this.loadSubLinkIntoCategories(customMenu[i]);
                                    }

                                    return (
                                        <PanelMenu
                                            // eslint-disable-next-line react/no-array-index-key
                                            key={index}
                                            currentUrl={currentPageUrl}
                                            closeOverlay={this.closeDrawer}
                                            panelTrigger={
                                                <PanelRow hasChildren {...styles.mainNavigationRow}>
                                                    <Typography {...styles.mainNavigationRowTypography}>
                                                        {link.title}
                                                    </Typography>
                                                </PanelRow>
                                            }
                                            menuItems={link.children}
                                            maxDepth={3}
                                            layer={0}
                                            {...styles.panelMenu}
                                            //
                                        />
                                    );
                                }
                                if (
                                    !!link.childrenType &&
                                    link.children &&
                                    link.children.filter(o => !o.excludeFromNavigation).length > 0
                                ) {
                                    const href =
                                        allowOpenParentLinks && link.url && link.url !== currentPageUrl
                                            ? link.url
                                            : undefined;
                                    return (
                                        <PanelMenu
                                            className="panel-menu"
                                            // eslint-disable-next-line react/no-array-index-key
                                            key={index}
                                            currentUrl={currentPageUrl}
                                            closeOverlay={this.closeDrawer}
                                            panelTrigger={
                                                <PanelRow
                                                    className="main-navigation-row"
                                                    href={href}
                                                    isCurrent={link.url === currentPageUrl}
                                                    hasChildren={true}
                                                    allowOpenParentLinks={allowOpenParentLinks}
                                                    {...styles.mainNavigationRow}
                                                >
                                                    <Typography
                                                        className="main-navigation-row-text"
                                                        {...styles.mainNavigationRowTypography}
                                                    >
                                                        {link.title}
                                                    </Typography>
                                                </PanelRow>
                                            }
                                            menuItems={link.children}
                                            maxDepth={link.maxDepth || 3}
                                            layer={0}
                                            allowOpenParentLinks={allowOpenParentLinks}
                                            {...styles.panelMenu}
                                        />
                                    );
                                }

                                return (
                                    <PanelRow
                                        // eslint-disable-next-line react/no-array-index-key
                                        key={index}
                                        onClick={this.closeDrawer}
                                        href={link.url}
                                        target={link.openInNewWindow ? "_blank" : ""}
                                        {...styles.mainNavigationRow}
                                        className="main-navigation-row"
                                    >
                                        <Typography
                                            className="main-navigation-row-text"
                                            {...styles.mainNavigationRowTypography}
                                        >
                                            {link.title}
                                        </Typography>
                                    </PanelRow>
                                );
                            })}
                        {!displayVmiNavigation && showQuickOrder && quickOrderLink && (
                            <PanelRow
                                className="main-navigation-row"
                                isCurrent={currentPageUrl === quickOrderLink.url}
                                onClick={this.closeDrawer}
                                href={quickOrderLink.url}
                                {...styles.mainNavigationRow}
                            >
                                <Typography
                                    className="main-navigation-row-text"
                                    {...styles.mainNavigationRowTypography}
                                >
                                    {quickOrderLink.title}
                                </Typography>
                            </PanelRow>
                        )}
                        {displayVmiNavigation &&
                            vmiPageLinks &&
                            vmiPageLinks.map(
                                link =>
                                    link && (
                                        <PanelRow
                                            className="main-navigation-row"
                                            key={link.id}
                                            isCurrent={currentPageUrl === link.url}
                                            onClick={this.closeDrawer}
                                            href={link.url}
                                            {...styles.mainNavigationRow}
                                        >
                                            <Typography
                                                className="main-navigation-row-text"
                                                {...styles.mainNavigationRowTypography}
                                            >
                                                {link.title}
                                            </Typography>
                                        </PanelRow>
                                    ),
                            )}
                    </StyledSection>
                    {this.props.headerLinkListLinks.length > 0 && (
                        <StyledSection className="drawer-section-wrapper" {...styles.drawerSectionWrapper}>
                            {this.props.headerLinkListLinks.map((link, index) => (
                                <PanelRow
                                    className="logo-links-row"
                                    key={link.title + String(index)}
                                    {...(styles.logoLinks && omitSingle(styles.logoLinks, "typographyProps"))}
                                    isCurrent={currentPageUrl === link.url}
                                    onClick={this.closeDrawer}
                                    href={link.url}
                                    target={link.openInNewWindow ? "_blank" : ""}
                                >
                                    <Typography className="logo-link-title" {...styles.logoLinks?.typographyProps}>
                                        {link.title}
                                    </Typography>
                                </PanelRow>
                            ))}
                        </StyledSection>
                    )}
                    <StyledSection className="drawer-section-wrapper" {...styles.drawerSectionWrapper}>
                        {/* <SelectorMenu
                            options={currencies?.map(c => {
                                return {
                                    title: c.currencyCode.toUpperCase(),
                                    clickableProps: {
                                        onClick: () => {
                                            setCurrency({ currencyId: c.id });
                                            this.closeDrawer();
                                        },
                                    },
                                };
                            })}
                            closeModal={this.closeDrawer}
                            currentOption={currencies?.find(c => c.id === currentCurrencyId)?.currencyCode}
                            currentOptionIcon={
                                <Typography {...styles.currencySymbol}>{currentCurrencySymbol}</Typography>
                            }
                        /> */}
                        <SelectorMenu
                            dataTestSelector="mobileLanguageSelector"
                            options={languages?.map(l => {
                                return {
                                    title: l.languageCode.toUpperCase(),
                                    clickableProps: {
                                        onClick: () => {
                                            setLanguage({ languageId: l.id });
                                            this.closeDrawer();
                                        },
                                    },
                                };
                            })}
                            currentOption={this.state.urlLangCodeState}
                            closeModal={this.closeDrawer}
                            currentOptionIcon={
                                currentLanguage?.imageFilePath ? (
                                    <LogoImage className="language-image" src={currentLanguage.imageFilePath} alt="" />
                                ) : (
                                    <Icon className="menu-row-icon" src={Globe} {...styles.menuRowIcon} />
                                )
                            }
                        />
                        {websiteDetails && websiteDetails.webSiteTitle && (
                            <StyledWrapper {...styles.websiteWrapper}>
                                <Clickable href={websiteDetails.webSiteLink} target="_blank">
                                    <LazyImage src={websiteDetails.webSiteFlag} />
                                    <Typography>{translate(websiteDetails.webSiteTitle)}</Typography>
                                </Clickable>
                            </StyledWrapper>
                        )}
                        {showCustomerMenuItem && (
                            <PanelMenu
                                className="panel-menu"
                                {...styles.panelMenu}
                                panelTrigger={
                                    <PanelRow
                                        hasChildren
                                        className="logo-links-row"
                                        {...styles.logoLinks}
                                        color="common.accent"
                                    >
                                        <Typography className="logo-link-title" {...styles.logoLinks?.typographyProps}>
                                            <Icon src={MapPin} className="menu-row-icon" {...styles.menuRowIcon} />
                                            {fulfillmentLabel}
                                        </Typography>
                                    </PanelRow>
                                }
                                layer={0}
                                closeOverlay={this.closeDrawer}
                            >
                                {isPunchOutSession ? (
                                    <PanelRow
                                        tabIndex={-1}
                                        className="change-customer-row"
                                        {...styles.changeCustomerRow}
                                    >
                                        {createWidgetElement("Header/AddressDrawerPunchOutCustomer", {
                                            fields: {},
                                        })}
                                    </PanelRow>
                                ) : (
                                    <PanelRow
                                        tabIndex={-1}
                                        className="change-customer-row"
                                        {...styles.changeCustomerRow}
                                    >
                                        <GridContainer
                                            className="change-customer-row-container"
                                            {...styles.changeCustomerRowContainer}
                                        >
                                            <GridItem
                                                className="fulfillment-method-grid-item"
                                                {...styles.fulfillmentMethodGridItem}
                                            >
                                                {createWidgetElement("Header/AddressDrawerFulfillmentMethodSelector", {
                                                    fields: {},
                                                })}
                                            </GridItem>
                                            <GridItem className="addresses-grid-item" {...styles.addressesGridItem}>
                                                {createWidgetElement("Header/AddressDrawerSelectCustomer", {
                                                    fields: {},
                                                })}
                                            </GridItem>
                                            <GridItem
                                                className="pick-up-address-grid-item"
                                                {...styles.pickUpAddressGridItem}
                                            >
                                                {createWidgetElement("Header/AddressDrawerPickUpLocationSelector", {
                                                    fields: {},
                                                })}
                                            </GridItem>
                                            <GridItem
                                                className="apply-button-grid-item"
                                                {...styles.applyButtonGridItem}
                                            >
                                                {createWidgetElement("Header/AddressDrawerApplyButton", { fields: {} })}
                                            </GridItem>
                                        </GridContainer>
                                    </PanelRow>
                                )}
                            </PanelMenu>
                        )}
                        {displayPricingAndInventoryToggles && (
                            <>
                                {(!isCompactHeaderFlyout || showFlyoutInventoryToggle) && (
                                    <ShowInventoryToggle extendedStyles={styles.showInventoryToggle} />
                                )}
                                {(!isCompactHeaderFlyout || showFlyoutPricingToggle) && (
                                    <ShowPricingToggle extendedStyles={styles.showPricingToggle} />
                                )}
                            </>
                        )}
                        {userName && !isGuest && (
                            <Link href="/QuickOrder" onClick={this.closeDrawer} {...styles.quickOrder}>
                                Quick Order
                            </Link>
                        )}
                        {userName && !isGuest && (
                            <PanelRow className="sign-out-row" onClick={this.onSignOutHandler} {...styles.signOutRow}>
                                <Typography className="sign-out-row-text" {...styles.signOutRowText}>
                                    {isPunchOutSession ? translate("Cancel PunchOut") : translate("Sign Out")}
                                </Typography>
                            </PanelRow>
                        )}
                    </StyledSection>
                </Drawer>
            </>
        );
    }
}

const LogoImage = styled.img`
    margin-right: 10px;
    height: 22px;
`;

interface SelectorMenuProps {
    options?: MenuMappedLink[];
    closeModal?: () => void;
    currentOption?: string;
    currentOptionIcon?: React.ReactNode;
    dataTestSelector?: string;
}

const SelectorMenu: React.FC<SelectorMenuProps> = ({
    options,
    closeModal,
    currentOption,
    currentOptionIcon,
    dataTestSelector,
}) => {
    if (!options || options.length <= 1) {
        return null;
    }

    const trigger = (
        <PanelRow className="logo-links-row" hasChildren {...styles.logoLinks}>
            <StyledSpan className="panel-section-wrapper" {...styles.panelSectionWrapper}>
                {currentOptionIcon}
                <Typography
                    className="logo-link-title"
                    data-test-selector={`${dataTestSelector}_currentOption`}
                    {...styles.logoLinks?.typographyProps}
                >
                    {currentOption}
                </Typography>
            </StyledSpan>
        </PanelRow>
    );

    return (
        <PanelMenu
            className="panel-menu"
            currentUrl={currentOption}
            data-test-selector={dataTestSelector}
            closeOverlay={closeModal}
            panelTrigger={trigger}
            menuItems={options}
            maxDepth={1}
            layer={0}
            {...styles.panelMenu}
        />
    );
};
function findVisibilityState(data: any): string | undefined {
    for (const key in data) {
        if (data[key].type === "Common/HeaderSignIn") {
            return data[key].generalFields.visibilityState;
        }
    }
    return undefined;
}
export default connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(NavigationDrawer)));
